import { IAnalyticsProvider } from './trackingService';
import { ICheckout, ICheckoutTotals, ILineItem } from "../types/types";
import config from "../utils/config";

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[] | undefined;
};

declare const window: WindowWithDataLayer;

class GoogleTagManagerProvider implements IAnalyticsProvider {
  eventMap: Record<string, (data: { checkout: ICheckout, totals: ICheckoutTotals }) => Promise<void>> = {
    'begin_checkout': this.beginCheckout.bind(this),
    'add_shipping_info': this.addShippingInfo.bind(this),
    'add_payment_info': this.addPaymentInfo.bind(this),
    'purchase': this.purchase.bind(this),
  };

  sentBeginCheckout: boolean = false;
  sentPurchase: boolean = false;

  async trackEvent(event: string, data: { checkout: ICheckout, totals: ICheckoutTotals }): Promise<void> {
    if (this.eventMap[event]) {
      await this.eventMap[event](data);
    }
  }

  private async pushToDataLayer(eventData: Record<string, any>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeoutId = window.setTimeout(resolve, 2000);
      try {
        if (!window.dataLayer) {
          window.dataLayer = [];
        }
        eventData.eventCallback = () => {
          window.clearTimeout(timeoutId);
          resolve();
        };
        eventData.eventTimeout = 2000;
        window.dataLayer.push(eventData);
      } catch (error) {
        window.clearTimeout(timeoutId);
        console.error(error);
        reject(error);
      }
    });
  }

  private toNumber(value: string | number | null | undefined): number {
    return Number(value ?? 0);
  }

  private mapToEventItem(item: ILineItem): Record<string, any> {
    const productId = item.productId?.replace("gid://shopify/Product/", '') ?? '';
    const variantId = item.variantId?.replace("gid://shopify/ProductVariant/", '') ?? '';
    const variantLabel = item.selectedOptions.map((option) => option.value).join(" / ");

    return {
      item_id: variantId,
      item_name: item.productTitle,
      item_brand: "Bushbuck",
      item_category: "General",
      item_variant: variantLabel || item.variantTitle,
      item_sku: item.sku,
      image: item.image.url,
      compare_at_price: item.comparePrice,
      item_url: item.url,
      requires_shipping: item.requiresShipping,
      available_for_sale: item.availableForSale,
      discount_note: item.discountNote,
      price: this.toNumber(item.price),
      quantity: item.quantity,
      product_id: productId,
      variant_id: variantId,
    };
  }

  private getEcommercePayload(data: { checkout: ICheckout, totals: ICheckoutTotals }): Record<string, any> {
    return {
      transaction_id: data.checkout.cartId,
      currency: data.totals.currencyCode,
      value: this.toNumber(data.totals.total),
      tax: this.toNumber(data.totals.tax),
      shipping: this.toNumber(data.totals.shipping),
      coupon: data.checkout.discountCode,
      items: data.checkout.lineItems.map(this.mapToEventItem.bind(this)),
    };
  }

  private getUserDataPayload(checkout: ICheckout): Record<string, any> {
    return {
      email_address: checkout.email,
      phone_number: checkout.phone,
      user_id: checkout.customerId ?? checkout.customer?.customerId,
    };
  }

  async beginCheckout(data: { checkout: ICheckout, totals: ICheckoutTotals }): Promise<void> {
    if (this.sentBeginCheckout) {
      return;
    }

    const eventData = {
      event: 'begin_checkout_stape',
      CheckoutURL: `${config.checkoutUrl}/checkout/${data.checkout.cartId.replace('gid://shopify/Cart/', '')}`,
      ecommerce: this.getEcommercePayload(data),
      user_data: this.getUserDataPayload(data.checkout),
    };

    this.sentBeginCheckout = true;
    if (config.environment !== 'production') {
      console.log('Sending begin_checkout_stape event to GTM', eventData);
    }

    await this.pushToDataLayer(eventData);
  }

  async addShippingInfo(data: { checkout: ICheckout, totals: ICheckoutTotals }): Promise<void> {
    const eventData = {
      event: 'add_shipping_info_stape',
      ecommerce: this.getEcommercePayload(data),
      user_data: this.getUserDataPayload(data.checkout),
    };

    if (config.environment !== 'production') {
      console.log('Sending add_shipping_info_stape event to GTM', eventData);
    }

    await this.pushToDataLayer(eventData);
  }

  async addPaymentInfo(data: { checkout: ICheckout, totals: ICheckoutTotals }): Promise<void> {
    const eventData = {
      event: 'add_payment_info_stape',
      ecommerce: this.getEcommercePayload(data),
      user_data: this.getUserDataPayload(data.checkout),
    };

    if (config.environment !== 'production') {
      console.log('Sending add_payment_info_stape event to GTM', eventData);
    }

    await this.pushToDataLayer(eventData);
  }

  async purchase(data: { checkout: ICheckout, totals: ICheckoutTotals }): Promise<void> {
    const cartId = data.checkout.cartId.replace('gid://shopify/Cart/', '');
    const hasSentPurchase = localStorage.getItem(`sentPurchase_${cartId}`);
    if (this.sentPurchase || hasSentPurchase) {
      return;
    }

    const eventData = {
      event: 'purchase_stape',
      ecommerce: this.getEcommercePayload(data),
      user_data: this.getUserDataPayload(data.checkout),
    };

    this.sentPurchase = true;
    if (config.environment !== 'production') {
      console.log('Sending purchase_stape event to GTM', eventData);
    }

    await this.pushToDataLayer(eventData);
    localStorage.setItem(`sentPurchase_${cartId}`, 'true');
  }
}

export default GoogleTagManagerProvider;
